import { useCallback, useEffect, useRef, useState } from "react";
import { ApolloError } from "@apollo/client";
import { nanoid } from "nanoid";

import {
	useStartChatStreamMutation,
	StartChatStreamMutationVariables
} from "graphql/mutations/start-chat-stream.generated";
import { useStopChatStreamMutation } from "graphql/mutations/stop-chat-stream.generated";
import { useGetWebAiChatMessagesQuery } from "graphql/queries/get-web-ai-chat-messages.generated";
import { useChatStreamWordsSubscription } from "graphql/subscriptions/chat-stream-words.generated";
import { WebAiChatMessageOutput } from "graphql/generated/graphql";

import { useChatStreamThoughts } from "hooks/useChatStreamThoughts";
import { useFileUpload } from "hooks/useFileUpload";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import {
	aiChatStatusType,
	clearAiChatStatus,
	getAiChatStatusVar,
	useAiChatStatus
} from "state/reactive-vars/ai-chat-status-var";

import { AiChatOrigin, AiChatType } from "constants/field-values";

// A message that is still being answered has no id from Doctrine yet, so it is marked with
// `isPlaceholder` and replaced by the saved message once the answer is stored. `attachments` are
// the files uploaded from the browser, before the server returns its own copy of them.
export interface AiChatMessage extends WebAiChatMessageOutput {
	isPlaceholder?: boolean;
	attachments?: Array<{
		id: string;
		fileName: string;
		mimeType: string;
		publicUrl?: string;
		fileType: string;
		fileKey?: string;
		gsUri?: string;
	}>;
}

// A shared constant. An inline `[]` would be a new array on every render and would invalidate
// every memo that depends on it.
const EMPTY_MESSAGES: AiChatMessage[] = [];

// `get_web_aichat_messages` returns this error code when the user does not own the chat, so that
// case can show its own message instead of the generic one.
const CHAT_FORBIDDEN_CODE = "chat-forbidden";

const CHAT_LOAD_ERROR_MESSAGE = "This conversation could not be loaded. Please try again.";

const STREAM_ERROR_MESSAGE = "An error occurred while generating the response. Please try again.";

// How long to wait for Doctrine to save the answer after its last word reached the browser.
const PERSISTED_ANSWER_DELAY_MS = 2000;

// When to warn the user that the answer is slow. The first timeout applies to a stream that stopped
// sending data. The second applies to a stream that has not sent any answer text at all, which
// usually means the model is still collecting data before it writes.
const LONG_RUNNING_MS = 60_000;
const LONG_RUNNING_NO_ANSWER_MS = 150_000;

// Maps a MIME type to the file type used by the API and by the attachment UI.
export const getAttachmentFileType = (mimeType: string): string =>
	mimeType.startsWith("image/")
		? "image"
		: mimeType.startsWith("video/")
			? "video"
			: mimeType.startsWith("audio/")
				? "audio"
				: mimeType === "application/pdf"
					? "pdf"
					: "document";

// Extra data the screen adds to a question. The conversation itself knows nothing about properties,
// devices or briefs: each screen decides what its own questions carry.
export interface SendOverrides {
	// Merged into the variables of the `start_chat_stream` mutation.
	variables?: Partial<StartChatStreamMutationVariables>;
	// Written to the chat status var together with the question and its placeholder.
	status?: aiChatStatusType;
}

export interface ServerMessagesInfo {
	// The `ai_chats` row returned with the messages, or null if the server did not send one.
	aiChat: any;
	threadId: string;
	// True if the list contains an answer, false if it only contains the question being answered.
	hasAnswer: boolean;
	// "send" = the list came from the mutation that sent the question. "load" = the thread was read.
	source: "send" | "load";
}

interface UseAiChatConversationOptions {
	// Which conversation this is. It selects the reactive var that stores the thread, the upload slot
	// used for its attachments, and the `chat_type` saved on the row.
	chatType: AiChatType;
	// Whether an unsent draft is kept when this component unmounts. A chat that is discarded on
	// navigation does not keep its draft.
	keepDraftOnUnmount?: boolean;
	/*
	Called after a message list from the server has been merged into the thread. Whatever it returns
	is written to the chat status in the same update, so a screen can keep its own fields on the row
	(the brief flags, the selections the chat was scoped to) without causing a second render.
	*/
	onServerMessages?: (info: ServerMessagesInfo) => aiChatStatusType | void;
}

/*
Handles the logic of an AI chat: the thread on screen, the question being answered, and the word
stream the answer arrives on. It renders nothing, so each screen can display the same conversation
with its own layout. That is the reason this is a hook and not a component.

The thread is stored in a reactive var instead of component state. An answer can take minutes, much
longer than a panel stays mounted, and the words are rows in the database. Keeping the thread and
the `streamId` outside the component is what allows the user to navigate away and come back to an
answer that is still being written, instead of an empty bubble.
*/
export const useAiChatConversation = ({
	chatType,
	keepDraftOnUnmount = true,
	onServerMessages
}: UseAiChatConversationOptions) => {
	const { loggedUserValue } = useLoggedUser();
	const statusVar = getAiChatStatusVar(chatType);
	const { aiChatStatusValue, updateAiChatStatus } = useAiChatStatus(chatType);
	const { chatId = "", chatMessages = EMPTY_MESSAGES } = aiChatStatusValue as typeof aiChatStatusValue & {
		chatMessages?: AiChatMessage[];
	};

	// Read from refs at call time: both are used inside callbacks whose closures were created before
	// the caller's latest render.
	const onServerMessagesRef = useRef(onServerMessages);
	onServerMessagesRef.current = onServerMessages;

	const keepDraftOnUnmountRef = useRef(keepDraftOnUnmount);
	keepDraftOnUnmountRef.current = keepDraftOnUnmount;

	// Kept in local state so typing does not re-render every consumer of the chat status var. It is
	// loaded from the var on mount and written back on unmount, which is what keeps an unsent draft
	// after navigation.
	const [draft, setDraft] = useState<string>(() => statusVar().chatText ?? "");
	const draftRef = useRef<string>("");
	draftRef.current = draft;

	const [streamingText, setStreamingText] = useState<string>("");
	const [streamingError, setStreamingError] = useState<string>("");
	const [loadError, setLoadError] = useState<string>("");
	// Initialised from the chat status var: a stream that was still open when the component unmounted
	// is resumed, instead of leaving the answer as an empty bubble.
	const [streamId, setStreamId] = useState<string>(() => statusVar().streamId ?? "");
	// True while the AI is answering. A resumed stream starts as true: the answer is still being
	// written even though this component was just mounted.
	const [isAiResponding, setIsAiResponding] = useState<boolean>(() => !!statusVar().streamId);
	// True when the word stream reported that it finished. From that point the answer is complete even
	// if the mutation that saves it never responds, so the placeholder shows the streamed text.
	const [isStreamComplete, setIsStreamComplete] = useState<boolean>(false);
	const [isLongRunning, setIsLongRunning] = useState<boolean>(false);

	const longRunningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const longRunningNoAnswerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (!keepDraftOnUnmountRef.current) return;

			statusVar({ ...statusVar(), chatText: draftRef.current });
		};
	}, [statusVar]);

	const { uploadingFiles, uploadFiles, removeFile, clearAllFiles, getCompletedFiles, isUploading, hasFiles } =
		useFileUpload(chatType);

	const { thoughts, isThinking } = useChatStreamThoughts(streamId);

	useChatStreamWordsSubscription({
		variables: { chat_id: streamId },
		skip: !streamId,
		shouldResubscribe: true,
		onData: args => {
			const words = args.data.data?.chat_stream_words;

			if (!words?.length) return;

			const spoken: string[] = [];
			let isComplete = false;

			for (const { word, is_final, has_error, error_message } of words) {
				if (has_error) {
					console.error("Chat stream error:", error_message);
					setStreamingError(error_message || STREAM_ERROR_MESSAGE);
					setStreamId("");
					setIsAiResponding(false);
					return;
				}

				if (is_final) isComplete = true;
				if (word) spoken.push(word);
			}

			setStreamingText(spoken.join(""));

			if (isComplete) {
				// The AI reported the end of the stream. The input is unlocked even if the mutation
				// that saves the answer is still running.
				setIsAiResponding(false);
				setIsStreamComplete(true);
			}
		},
		onError: error => {
			console.error("Chat stream subscription error:", error);
			// Unlock the input if the subscription fails, so the user is not blocked.
			setIsAiResponding(false);
		}
	});

	useEffect(() => {
		if (streamId) {
			setStreamingError("");
			setIsStreamComplete(false);
		}
	}, [streamId]);

	// Starts the "taking a while" warning. The timer is reset by every chunk received, either a word
	// of the answer or a line of the reasoning: a stream that is still sending data is not stuck.
	useEffect(() => {
		if (longRunningTimerRef.current) {
			clearTimeout(longRunningTimerRef.current);
			longRunningTimerRef.current = null;
		}

		if (!streamId) {
			setIsLongRunning(false);
			return;
		}

		setIsLongRunning(false);
		longRunningTimerRef.current = setTimeout(() => setIsLongRunning(true), LONG_RUNNING_MS);

		return () => {
			if (longRunningTimerRef.current) clearTimeout(longRunningTimerRef.current);
		};
	}, [streamId, streamingText, thoughts]);

	// The same warning with a longer timeout, for a stream that has not sent any answer text yet.
	useEffect(() => {
		if (longRunningNoAnswerTimerRef.current) {
			clearTimeout(longRunningNoAnswerTimerRef.current);
			longRunningNoAnswerTimerRef.current = null;
		}

		if (!streamId || streamingText.length > 0) return;

		longRunningNoAnswerTimerRef.current = setTimeout(() => setIsLongRunning(true), LONG_RUNNING_NO_ANSWER_MS);

		return () => {
			if (longRunningNoAnswerTimerRef.current) clearTimeout(longRunningNoAnswerTimerRef.current);
		};
	}, [streamId, streamingText]);

	/*
	Merges a message list returned by the server into the thread. The server can respond before the AI
	has written anything: the chat row already exists, so the thread id can be used, but the answer is
	still streaming. Overwriting the conversation in that case would delete the placeholder that the
	words are being written into.
	*/
	const applyServerMessages = useCallback(
		(serverMessages: any[], source: ServerMessagesInfo["source"]) => {
			const threadId = serverMessages[0]?.chatId;
			const aiChat = serverMessages[0]?.aiChat;
			const hasAnswer = serverMessages.some(message => message?.isSystem);

			// Read from the var and not from the rendered value: this runs inside a mutation callback
			// whose closure was created before the question and its placeholder were added.
			const currentMessages = (statusVar().chatMessages || EMPTY_MESSAGES) as AiChatMessage[];

			// Keep only the user's messages from OTHER threads; the server returns the rest.
			const messagesToKeep = currentMessages.filter(
				message => !message.isSystem && message.chatId !== threadId && currentMessages.indexOf(message) !== 0
			);

			// If the server sent an answer, its list is the full conversation. If it did not, keep what is
			// already on screen, except when the chat is still empty, where showing only the question is
			// better than showing nothing.
			const messagesUpdate = hasAnswer
				? [...messagesToKeep, ...serverMessages]
				: currentMessages.length
					? null
					: serverMessages;

			const callerStatus = onServerMessagesRef.current?.({ aiChat, threadId, hasAnswer, source }) || {};

			updateAiChatStatus({
				chatTitle: aiChat?.doctrine_title || "",
				...(messagesUpdate && { chatMessages: messagesUpdate }),
				// The saved answer replaces the stream that wrote it.
				...(hasAnswer && { streamId: "" }),
				...(source === "send" && { chatId: threadId }),
				...callerStatus
			});

			return { hasAnswer };
		},
		[statusVar, updateAiChatStatus]
	);

	const [startChatStreamMutation] = useStartChatStreamMutation({
		errorPolicy: "none",
		notifyOnNetworkStatusChange: true,
		onCompleted: data => {
			if (!data.start_chat_stream?.length) return;

			applyServerMessages(data.start_chat_stream, "send");
			/*
			The lock is NOT released here. The mutation responds as soon as the thread exists, while the
			AI keeps sending words over the subscription. It is released by the final marker of the
			stream, by an error, by a stop, or by starting a new thread.
			*/
		},
		onError: (error: ApolloError) => {
			console.log("Start chat stream error:", error);
			// Unlock the input so the user can retry after a failed mutation.
			setIsAiResponding(false);
		}
	});

	const [stopChatStreamMutation] = useStopChatStreamMutation();

	const { loading: isLoadingMessages, refetch: refetchMessages } = useGetWebAiChatMessagesQuery({
		skip: !chatId,
		variables: { doctrine_chat_id: chatId },
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onCompleted: data => {
			setLoadError("");

			if (!data.get_web_aichat_messages?.length) return;

			const { hasAnswer } = applyServerMessages(data.get_web_aichat_messages, "load");

			// Only a saved answer replaces the streamed one. A thread reopened while the AI is still
			// writing comes back without the answer, and clearing here would erase the resumed stream.
			if (hasAnswer) setStreamingText("");
		},
		onError: (error: ApolloError) => {
			console.log("Get web AI chat messages error:", error);

			// A chat owned by another user is refused with a specific message, and that message is written
			// for the user. Every other failure is an infrastructure problem, so it shows the generic one.
			const forbidden = error.graphQLErrors?.find(
				graphQLError => graphQLError.extensions?.code === CHAT_FORBIDDEN_CODE
			);
			setLoadError(forbidden ? forbidden.message : CHAT_LOAD_ERROR_MESSAGE);
		}
	});

	// Clear the error when the conversation changes: it belongs to the chat that failed to load.
	useEffect(() => {
		setLoadError("");
	}, [chatId]);

	/*
	The response of the mutation that saves an answer can be lost: the gateway times out long before a
	long answer is finished. The answer is saved anyway once the stream closes, so the thread is read
	again to replace the placeholder with the real message, with its sources and feedback controls.
	*/
	useEffect(() => {
		const hasPlaceholder = chatMessages.some(message => message.isPlaceholder);

		if (!isStreamComplete || !chatId || !hasPlaceholder) return;

		// Doctrine saves the message shortly after the last word arrives. Reading too early returns
		// only the question.
		const timer = setTimeout(
			() => refetchMessages().catch(error => console.log("Refetch chat messages error:", error)),
			PERSISTED_ANSWER_DELAY_MS
		);

		return () => clearTimeout(timer);
	}, [isStreamComplete, chatId, chatMessages, refetchMessages]);

	// Sends the question. Returns true if it was sent, so the caller does not set its own "sending"
	// state when the conversation rejects the question.
	const send = useCallback(
		(messageText?: string, overrides?: SendOverrides): boolean => {
			// Files that are still uploading would be sent without their content, and an answer that is
			// still being written would be interrupted by a second question on the same stream.
			if (isUploading || isAiResponding) return false;

			const text = messageText || draftRef.current;

			if (!text || !loggedUserValue?.id) return false;

			setIsAiResponding(true);

			const completedFiles = getCompletedFiles();
			const attachments = completedFiles.map(file => ({
				id: file.id,
				fileName: file.fileName,
				mimeType: file.mimeType,
				publicUrl: file.publicUrl || "",
				fileType: getAttachmentFileType(file.mimeType),
				fileKey: file.fileKey,
				gsUri: file.gsUri
			}));

			/*
			The stream id is created before the question is sent, because the subscription must already be
			listening when the first word arrives: Doctrine's own message id does not exist until the
			answer is written. Inside an existing thread the id is prefixed with the thread id, so two
			questions in the same conversation never share a stream.
			*/
			const messageStreamId = chatId ? `${chatId}_${nanoid()}` : nanoid();

			const question = {
				id: `user-message-${Date.now()}`,
				isSystem: false,
				message: text,
				createdAt: "",
				chatId,
				user: loggedUserValue,
				aiChatSources: [],
				aiChatSources_aggregate: { aggregate: undefined, nodes: [] },
				attachments
			} as AiChatMessage;

			// The answer is written into this message, word by word, until the saved message replaces it.
			const placeholder = {
				id: `placeholder-${Date.now()}`,
				isSystem: true,
				message: "",
				createdAt: "",
				chatId,
				user: null,
				aiChatSources: [],
				aiChatSources_aggregate: { aggregate: undefined, nodes: [] },
				isPlaceholder: true
			} as AiChatMessage;

			updateAiChatStatus({
				chatMessages: [...chatMessages, question, placeholder],
				streamId: messageStreamId,
				...overrides?.status
			});

			setDraft("");
			// Cleared here, otherwise the previous streamed text would appear inside the new placeholder.
			setStreamingText("");
			setStreamingError("");
			clearAllFiles();
			setStreamId(messageStreamId);

			startChatStreamMutation({
				variables: {
					message: text,
					user_id: loggedUserValue.id,
					workspace_id: loggedUserValue.workspace_id,
					organization_id: loggedUserValue.organization_id,
					stream_id: messageStreamId,
					doctrine_chat_id: chatId || undefined,
					chat_type: chatType,
					chat_origin: AiChatOrigin.PRIMARY,
					files: completedFiles.length
						? completedFiles.map(file => ({
								fileKey: file.fileKey,
								fileName: file.fileName,
								fileType: getAttachmentFileType(file.mimeType),
								mimeType: file.mimeType,
								gsUri: file.gsUri,
								publicUrl: file.publicUrl
							}))
						: undefined,
					...overrides?.variables
				}
			});

			return true;
		},
		[
			chatId,
			chatMessages,
			chatType,
			clearAllFiles,
			getCompletedFiles,
			isAiResponding,
			isUploading,
			loggedUserValue,
			startChatStreamMutation,
			updateAiChatStatus
		]
	);

	const stop = useCallback(() => {
		const stoppedStreamId = streamId;

		setStreamId("");
		setStreamingText("");
		setIsLongRunning(false);
		setIsAiResponding(false);
		// There is nothing left to resume after navigation.
		updateAiChatStatus({ streamId: "" });

		if (longRunningTimerRef.current) {
			clearTimeout(longRunningTimerRef.current);
			longRunningTimerRef.current = null;
		}

		if (longRunningNoAnswerTimerRef.current) {
			clearTimeout(longRunningNoAnswerTimerRef.current);
			longRunningNoAnswerTimerRef.current = null;
		}

		if (stoppedStreamId) {
			stopChatStreamMutation({ variables: { stream_id: stoppedStreamId } }).catch(error =>
				console.log("Stop chat stream error:", error)
			);
		}
	}, [streamId, stopChatStreamMutation, updateAiChatStatus]);

	const startNewThread = useCallback(() => {
		clearAiChatStatus(chatType);
		setDraft("");
		// Otherwise files staged for the previous thread would be sent with the first new message.
		clearAllFiles();
		setStreamId("");
		setStreamingText("");
		setStreamingError("");
		setIsAiResponding(false);
	}, [chatType, clearAllFiles]);

	// Reopens a saved conversation from the history. A stream left from the previous thread would
	// write its words into this one, so it is cleared together with the messages on screen.
	const openThread = useCallback(
		(doctrineChatId: string) => {
			if (doctrineChatId === chatId) {
				updateAiChatStatus({ historyOpen: false });
				return;
			}

			setStreamId("");
			setStreamingText("");
			setStreamingError("");
			setIsAiResponding(false);
			updateAiChatStatus({
				chatId: doctrineChatId,
				chatMessages: [],
				chatTitle: "",
				streamId: "",
				historyOpen: false
			});
		},
		[chatId, updateAiChatStatus]
	);

	const setHistoryOpen = useCallback(
		(open: boolean) => updateAiChatStatus({ historyOpen: open }),
		[updateAiChatStatus]
	);

	const attachFiles = useCallback(
		async (files: FileList | null) => {
			if (files?.length) await uploadFiles(files);
		},
		[uploadFiles]
	);

	return {
		// The thread
		chatId,
		messages: chatMessages,
		isLoadingMessages,
		loadError,

		// The question being answered
		draft,
		setDraft,
		send,
		stop,
		startNewThread,
		openThread,
		isAiResponding,
		isLongRunning,

		// The answer arriving
		streamId,
		streamingText,
		streamingError,
		isStreamComplete,
		thoughts,
		isThinking,

		// Attachments
		uploadingFiles,
		attachFiles,
		removeFile,
		isUploading,
		hasFiles,

		// History
		historyOpen: aiChatStatusValue.historyOpen ?? false,
		setHistoryOpen
	};
};
